import { useEffect, useMemo, useRef, useState } from "react";
import { useCallback } from "react";
import type { IdentifyResponse, Species } from "../lib/types";
import { ApiError, identify, identifyPhoto, identifyVideo } from "../lib/api";
import { downscaleToDataUrl } from "../lib/image";
import { extractFrames } from "../lib/video";
import { logEvent } from "../lib/events";
import { useModalChrome } from "../lib/modalUx";
import { scheduleDeletePhotoUrl } from "../lib/storage";
import { MatchCard } from "./MatchCard";
import { QualityNote } from "./QualityNote";
import { CameraIcon, CloseIcon } from "../ui";

export type Mode = "describe" | "photo" | "video";

// Turn a raw identify error into UI copy the user can act on. Rate-limit
// hits look identical to real failures unless we say so explicitly.
function friendlyIdentifyError(err: unknown): string {
  if (err instanceof ApiError && err.status === 429) {
    const secs = err.retryAfter && err.retryAfter > 0 ? err.retryAfter : 0;
    return secs > 0
      ? `You're going too fast — try again in ${Math.max(1, Math.ceil(secs / 60))} min.`
      : "You're going too fast — try again in a few seconds.";
  }
  return err instanceof Error ? err.message : "Identification failed.";
}

export function DescribeModal({
  open,
  onClose,
  locationName,
  candidates,
  isSeen,
  onAddSpecies,
  initialMode = "describe",
}: {
  open: boolean;
  onClose: () => void;
  locationName: string;
  candidates: Species[];
  isSeen: (species: Species) => boolean;
  onAddSpecies: (s: Species, photoUrl?: string) => void;
  initialMode?: Mode;
}) {
  const [mode, setMode] = useState<Mode>(initialMode);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Sync to the caller's requested mode on the open transition, during render
  // (not in an effect) so the very first opened render is already in that mode
  // — opening straight to "photo" must not flash the describe textarea, whose
  // autoFocus would grab then drop focus when the textarea unmounts.
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) setMode(initialMode);
  }
  const [description, setDescription] = useState("");
  const [photo, setPhoto] = useState<string | null>(null);
  const [result, setResult] = useState<IdentifyResponse | null>(null);
  const [userPhotoUrl, setUserPhotoUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Which frame the video extractor is on, so users see "Frame 2 of 3…"
  // instead of a stuck spinner on a malformed clip.
  const [videoFrameProgress, setVideoFrameProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const videoAbortRef = useRef<AbortController | null>(null);
  const abortVideoPrep = useCallback(() => {
    videoAbortRef.current?.abort();
    videoAbortRef.current = null;
  }, []);

  // identify-photo persists the upload to R2 up front, before the user
  // decides whether to log the result. Track the pending (not-yet-logged)
  // photo so we can reclaim it if the modal is dismissed or a new photo is
  // identified without logging this one — otherwise every curiosity-ID that
  // isn't saved orphans an R2 object forever. Cleared when the photo is
  // attached to a lifelist entry. See identify-photo-r2-orphans-never-reclaimed.
  const pendingPhotoRef = useRef("");
  const discardPendingPhoto = useCallback(() => {
    if (pendingPhotoRef.current) {
      scheduleDeletePhotoUrl(pendingPhotoRef.current);
      pendingPhotoRef.current = "";
    }
  }, []);
  const handleClose = useCallback(() => {
    discardPendingPhoto();
    onClose();
  }, [discardPendingPhoto, onClose]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, handleClose]);

  useModalChrome(open, handleClose, dialogRef);

  const byScientific = useMemo(() => {
    const m = new Map<string, Species>();
    for (const s of candidates) {
      m.set(s.scientific_name.trim().toLowerCase(), s);
    }
    return m;
  }, [candidates]);

  const [videoFrames, setVideoFrames] = useState<string[] | null>(null);

  // Abort in-flight frame extraction if the modal closes mid-prep so the
  // seek loop can't outlive the modal.
  useEffect(() => {
    if (!open) abortVideoPrep();
  }, [open, abortVideoPrep]);

  // Every hook MUST be above this early return — a hook placed below it
  // runs only when the modal is open, so opening/closing changes the hook
  // count and React throws #310 ("rendered fewer hooks than expected"),
  // which is what was blanking the Describe modal. See
  // describe-modal-hooks-after-early-return.
  if (!open) return null;

  const candidatePayload = candidates.map((s) => ({
    common: s.common_name,
    scientific: s.scientific_name,
    count: s.observation_count,
  }));

  const runDescribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim() || loading) return;
    setLoading(true);
    setError(null);
    setResult(null);
    const t0 = performance.now();
    try {
      const res = await identify({
        description: description.trim(),
        location_name: locationName,
        candidates: candidatePayload,
      });
      setResult(res);
      const top = res.matches[0];
      logEvent("camera.identify.describe", {
        location_name: locationName,
        description_length: description.trim().length,
        candidates_offered: candidatePayload.length,
        matches_returned: res.matches.length,
        top_scientific: top?.scientific_name ?? "",
        top_common: top?.common_name ?? "",
        top_confidence: top?.confidence ?? 0,
        top_in_candidates: top
          ? candidatePayload.some(
              (c) =>
                c.scientific.trim().toLowerCase() ===
                top.scientific_name.trim().toLowerCase(),
            )
          : false,
        duration_ms: Math.round(performance.now() - t0),
      });
    } catch (err) {
      setError(friendlyIdentifyError(err));
      logEvent("camera.identify.describe.error", {
        location_name: locationName,
        message: err instanceof Error ? err.message : String(err),
        rate_limited: err instanceof ApiError && err.status === 429,
        duration_ms: Math.round(performance.now() - t0),
      });
    } finally {
      setLoading(false);
    }
  };

  const onPickPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    setError(null);
    setResult(null);
    setPhoto(null);
    setPreparing(true);
    try {
      setPhoto(await downscaleToDataUrl(file));
    } catch (err) {
      // Record HEIC panics / oversized files so we can spot patterns
      // instead of only seeing the toast that vanishes.
      logEvent("prepare.photo.error", {
        file_type: file.type || "unknown",
        file_size: file.size,
        message: err instanceof Error ? err.message : String(err),
      });
      setError(err instanceof Error ? err.message : "Could not read photo.");
    } finally {
      setPreparing(false);
    }
  };

  const onPickVideo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError(null);
    setResult(null);
    setVideoFrames(null);
    setVideoFrameProgress({ done: 0, total: 3 });
    setPreparing(true);
    const ctrl = new AbortController();
    videoAbortRef.current = ctrl;
    try {
      const frames = await extractFrames(file, 3, {
        signal: ctrl.signal,
        onProgress: (done, total) => setVideoFrameProgress({ done, total }),
      });
      setVideoFrames(frames);
    } catch (err) {
      logEvent("prepare.video.error", {
        file_type: file.type || "unknown",
        file_size: file.size,
        aborted: ctrl.signal.aborted,
        message: err instanceof Error ? err.message : String(err),
      });
      if (!ctrl.signal.aborted) {
        setError(
          err instanceof Error
            ? err.message
            : "Couldn't extract frames from that video.",
        );
      }
    } finally {
      videoAbortRef.current = null;
      setPreparing(false);
      setVideoFrameProgress(null);
    }
  };

  const runVideo = async () => {
    if (!videoFrames || videoFrames.length === 0 || loading) return;
    setLoading(true);
    setError(null);
    setResult(null);
    const t0 = performance.now();
    try {
      const res = await identifyVideo({
        frames: videoFrames,
        location_name: locationName,
        candidates: candidatePayload,
      });
      setResult({ matches: res.matches, meta: res.meta });
      const top = res.matches[0];
      logEvent("camera.identify.video", {
        location_name: locationName,
        candidates_offered: candidatePayload.length,
        frames_sent: videoFrames.length,
        frames_analysed: res.frames_analysed,
        matches_returned: res.matches.length,
        top_scientific: top?.scientific_name ?? "",
        top_confidence: top?.confidence ?? 0,
        duration_ms: Math.round(performance.now() - t0),
      });
    } catch (err) {
      setError(friendlyIdentifyError(err));
      logEvent("camera.identify.video.error", {
        location_name: locationName,
        message: err instanceof Error ? err.message : String(err),
        rate_limited: err instanceof ApiError && err.status === 429,
        duration_ms: Math.round(performance.now() - t0),
      });
    } finally {
      setLoading(false);
    }
  };

  const runPhoto = async () => {
    if (!photo || loading) return;
    // A prior identify that was never logged is now abandoned — reclaim it
    // before this new upload replaces it as the pending photo.
    discardPendingPhoto();
    setLoading(true);
    setError(null);
    setResult(null);
    const t0 = performance.now();
    try {
      const res = await identifyPhoto({
        image: photo,
        location_name: locationName,
        candidates: candidatePayload,
      });
      setUserPhotoUrl(res.photo_url);
      pendingPhotoRef.current = res.photo_url;
      setResult(res);
      const top = res.matches[0];
      logEvent("camera.identify.photo", {
        location_name: locationName,
        candidates_offered: candidatePayload.length,
        matches_returned: res.matches.length,
        top_scientific: top?.scientific_name ?? "",
        top_common: top?.common_name ?? "",
        top_confidence: top?.confidence ?? 0,
        top_in_candidates: top
          ? candidatePayload.some(
              (c) =>
                c.scientific.trim().toLowerCase() ===
                top.scientific_name.trim().toLowerCase(),
            )
          : false,
        duration_ms: Math.round(performance.now() - t0),
      });
    } catch (err) {
      setError(friendlyIdentifyError(err));
      logEvent("camera.identify.photo.error", {
        location_name: locationName,
        message: err instanceof Error ? err.message : String(err),
        rate_limited: err instanceof ApiError && err.status === 429,
        duration_ms: Math.round(performance.now() - t0),
      });
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    discardPendingPhoto();
    setDescription("");
    setPhoto(null);
    setUserPhotoUrl("");
    setVideoFrames(null);
    setResult(null);
    setError(null);
  };

  const switchMode = (m: Mode) => {
    if (m === mode) return;
    setMode(m);
    setResult(null);
    setError(null);
  };

  const tabCls = (m: Mode) =>
    `flex-1 inline-flex min-h-[44px] items-center justify-center rounded-full px-3 py-2 text-sm font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aqua ${
      mode === m ? "bg-aqua-deep text-white shadow-card" : "text-ink/60"
    }`;

  return (
    <div
      className="fixed inset-0 z-30 flex items-end justify-center bg-ink/40 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={handleClose}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="flex max-h-[92dvh] w-full max-w-lg flex-col overflow-hidden overscroll-contain rounded-t-3xl bg-paper pb-[env(safe-area-inset-bottom)] shadow-card focus:outline-none sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Identify what you saw"
      >
        <div className="flex items-center justify-between border-b border-ink/5 px-5 py-4">
          <div>
            <h2 className="text-lg font-bold text-ink">
              Identify what you saw
            </h2>
            <p className="text-xs text-ink/60">{locationName}</p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="flex h-11 w-11 items-center justify-center rounded-full text-ink/60 hover:bg-ink/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aqua"
            aria-label="Close"
          >
            <CloseIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="flex gap-1 border-b border-ink/5 px-5 py-3">
          <button
            type="button"
            className={tabCls("describe")}
            onClick={() => switchMode("describe")}
          >
            Describe
          </button>
          <button
            type="button"
            className={tabCls("photo")}
            onClick={() => switchMode("photo")}
          >
            Photo
          </button>
          <button
            type="button"
            className={tabCls("video")}
            onClick={() => switchMode("video")}
          >
            Video
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {mode === "video" ? (
            <div>
              {videoFrames && videoFrames.length > 0 ? (
                <div className="grid grid-cols-3 gap-1.5">
                  {videoFrames.map((f, i) => (
                    <img
                      key={i}
                      src={f}
                      alt={`Frame ${i + 1}`}
                      className="aspect-square w-full rounded-xl bg-ink/5 object-cover"
                    />
                  ))}
                </div>
              ) : preparing ? (
                <div className="flex min-h-[120px] flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-ink/15 px-4 text-center text-sm text-ink/55">
                  <span>
                    {videoFrameProgress
                      ? `Frame ${videoFrameProgress.done} of ${videoFrameProgress.total}…`
                      : "Pulling frames from the video…"}
                  </span>
                  <button
                    type="button"
                    onClick={abortVideoPrep}
                    className="inline-flex min-h-[44px] items-center justify-center rounded-full px-4 py-2 text-sm font-bold uppercase tracking-wide text-coral hover:bg-coral/10"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <label className="flex min-h-[120px] cursor-pointer flex-col items-center justify-center gap-1 rounded-2xl border-2 border-dashed border-ink/15 px-4 py-8 text-center text-sm text-ink/55 hover:border-aqua-deep/50 hover:text-ink/70">
                  <CameraIcon className="h-6 w-6 text-aqua-deep" />
                  <span className="font-semibold text-ink/70">
                    Take or choose a video
                  </span>
                  <span className="text-xs">
                    We'll grab 3 stills and identify against the fish likely at this spot.
                  </span>
                  <input
                    type="file"
                    accept="video/*"
                    capture="environment"
                    className="hidden"
                    onChange={onPickVideo}
                  />
                </label>
              )}
              <div className="mt-3 flex justify-end gap-2">
                {(videoFrames || result) && (
                  <button type="button" className="btn-ghost" onClick={reset}>
                    Start over
                  </button>
                )}
                <button
                  type="button"
                  className="btn-accent"
                  onClick={runVideo}
                  disabled={loading || preparing || !videoFrames}
                >
                  {preparing
                    ? "Preparing…"
                    : loading
                      ? "Looking…"
                      : "Identify"}
                </button>
              </div>
            </div>
          ) : mode === "describe" ? (
            <form onSubmit={runDescribe}>
              <textarea
                className="field min-h-[96px] resize-y"
                placeholder="small orange octopus that changed color, or a yellow fish with black stripes hanging around the coral"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={2000}
                autoFocus
              />
              <div className="mt-3 flex justify-end gap-2">
                {result && (
                  <button type="button" className="btn-ghost" onClick={reset}>
                    Start over
                  </button>
                )}
                <button
                  type="submit"
                  className="btn-accent"
                  disabled={loading || !description.trim()}
                >
                  {loading ? "Thinking…" : "Identify"}
                </button>
              </div>
            </form>
          ) : (
            <div>
              {photo ? (
                <img
                  src={photo}
                  alt="Your photo"
                  className="max-h-64 w-full rounded-2xl object-contain bg-ink/5"
                />
              ) : preparing ? (
                <div className="flex min-h-[120px] items-center justify-center rounded-2xl border-2 border-dashed border-ink/15 text-sm text-ink/55">
                  Preparing photo…
                </div>
              ) : (
                <label className="flex min-h-[120px] cursor-pointer flex-col items-center justify-center gap-1 rounded-2xl border-2 border-dashed border-ink/15 px-4 py-8 text-center text-sm text-ink/55 hover:border-aqua-deep/50 hover:text-ink/70">
                  <CameraIcon className="h-6 w-6 text-aqua-deep" />
                  <span className="font-semibold text-ink/70">
                    Take or choose a photo
                  </span>
                  <span className="text-xs">
                    It’s identified against this spot’s species.
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={onPickPhoto}
                  />
                </label>
              )}
              <div className="mt-3 flex justify-end gap-2">
                {(photo || result) && (
                  <button type="button" className="btn-ghost" onClick={reset}>
                    Start over
                  </button>
                )}
                <button
                  type="button"
                  className="btn-accent"
                  onClick={runPhoto}
                  disabled={loading || preparing || !photo}
                >
                  {preparing
                    ? "Preparing…"
                    : loading
                      ? "Looking…"
                      : "Identify"}
                </button>
              </div>
            </div>
          )}

          {error && (
            <p className="mt-4 rounded-xl bg-coral/10 px-4 py-3 text-sm text-coral">
              {error}
            </p>
          )}

          {result && result.matches.length === 0 && !error && (
            <>
              <p className="mt-4 text-sm text-ink/60">
                {mode === "photo"
                  ? "No confident matches. Try a sharper, closer photo of the animal."
                  : "No confident matches. Try adding color, size, shape, or behavior details."}
              </p>
              <QualityNote meta={result.meta} />
            </>
          )}

          {result && result.matches.length > 0 && (
            <div className="mt-4 space-y-3">
              {result.matches.map((m, i) => {
                const sp = byScientific.get(
                  m.scientific_name.trim().toLowerCase(),
                );
                return (
                  <MatchCard
                    key={`${m.scientific_name}-${i}`}
                    match={m}
                    species={sp}
                    // Membership is by scientific name (lifelistEntryFor),
                    // so an OFF-LIST match (sp undefined) that's already in
                    // the lifelist must still read as seen. Falling back to
                    // `false` showed "I saw this one" on species already
                    // logged and let them be re-added. isSeen only reads
                    // scientific_name, so a name-only stand-in is safe. See
                    // describe-offlist-match-seen-always-false.
                    seen={isSeen(
                      sp ??
                        ({ scientific_name: m.scientific_name } as unknown as Species),
                    )}
                    onAdd={(resolved) => {
                      const attach =
                        mode === "photo" && userPhotoUrl
                          ? userPhotoUrl
                          : undefined;
                      // Logged → the photo is now referenced by the lifelist
                      // entry, so it must NOT be reclaimed on close.
                      if (attach) pendingPhotoRef.current = "";
                      onAddSpecies(resolved, attach);
                    }}
                  />
                );
              })}
              <QualityNote meta={result.meta} />
            </div>
          )}

          {mode === "photo" && result && !error && (
            <div
              className={`mt-4 rounded-xl px-4 py-3 text-sm ${
                result.matches.length === 0 || result.meta.quality !== "ok"
                  ? "bg-aqua/15 text-ink/70"
                  : "text-ink/55"
              }`}
            >
              {result.matches.length === 0 || result.meta.quality !== "ok"
                ? "Photo ID wasn't confident here. "
                : "Not the right match? "}
              <button
                type="button"
                onClick={() => switchMode("describe")}
                className="font-semibold text-aqua-deep underline-offset-2 hover:underline"
              >
                Describe it in words instead →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
